import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBackPages, type BackRecord } from '@/api/back';
import DateRangePicker from '@/components/base/DateRangePicker';
import Pagination from '@/components/base/Pagination';

const PAGE_SIZE = 20;

const typeMap: Record<string, { label: string; icon: string; color: string }> = {
  退货: { label: '退货', icon: 'ri-arrow-go-back-line', color: 'bg-primary-100 text-primary-700' },
  换货: { label: '换货', icon: 'ri-loop-left-line', color: 'bg-accent-100 text-accent-700' },
};

const statusMap: Record<string, { label: string; icon: string; color: string }> = {
  申请中: { label: '申请中', icon: 'ri-time-line', color: 'bg-secondary-100 text-secondary-800' },
  已确认: { label: '已确认', icon: 'ri-check-line', color: 'bg-accent-100 text-accent-800' },
  已完成: { label: '已完成', icon: 'ri-checkbox-circle-line', color: 'bg-accent-100 text-accent-800' },
  已拒绝: { label: '已拒绝', icon: 'ri-close-circle-line', color: 'bg-primary-100 text-primary-800' },
};

const typeFilters = [
  { id: 'all', label: '全部' },
  { id: '退货', label: '退货' },
  { id: '换货', label: '换货' },
];

/** 售后横向进度步骤（样式对齐订单详情订单进度） */
const BACK_STEPS = [
  { key: 'submit', label: '提交申请' },
  { key: 'check', label: '售后确认' },
  { key: 'exchange', label: '退换货' },
  { key: 'done', label: '售后完成' },
];

/** 已完成的步骤数：index < count 为已完成；count 指向当前步骤 */
function getBackCompletedCount(state: string): number {
  switch (state) {
    case '申请中':
      return 1;
    case '已确认':
      return 2;
    case '已完成':
      return 4;
    case '已拒绝':
      return 1;
    default:
      return 1;
  }
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  const d = new Date(value.replace(/-/g, '/'));
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatDatetime(value?: string | null) {
  if (!value) return '';
  const d = new Date(String(value).replace(/-/g, '/'));
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export default function AfterSales() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [list, setList] = useState<BackRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const fetchList = useCallback(async (pageNum: number) => {
    setLoading(true);
    setLoadError('');
    try {
      const date_range =
        dateFrom || dateTo
          ? [
              dateFrom || '1970-01-01',
              dateTo || '2999-12-31',
            ]
          : undefined;

      const res = await getBackPages({
        page: pageNum,
        pagenumber: PAGE_SIZE,
        search: searchKeyword.trim() || undefined,
        date_range,
        type: activeFilter !== 'all' ? activeFilter : undefined,
      });

      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setLoadError(res.message || res.msg || '售后列表加载失败');
        setList([]);
        setTotal(0);
        return;
      }

      const rows = Array.isArray(res.data) ? res.data : [];
      const nextTotal = Number(res.total ?? 0) || 0;
      setList(rows);
      setTotal(nextTotal);

      const pages = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));
      if (nextTotal > 0 && pageNum > pages) {
        setCurrentPage(pages);
      }
    } catch {
      setLoadError('售后列表加载失败，请稍后重试');
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, dateFrom, dateTo, searchKeyword]);

  useEffect(() => {
    void fetchList(currentPage);
  }, [currentPage, fetchList]);

  useEffect(() => {
    setCurrentPage((p) => (p === 1 ? p : 1));
  }, [activeFilter, dateFrom, dateTo, searchKeyword]);

  const hasActiveFilters = !!(searchKeyword || dateFrom || dateTo || activeFilter !== 'all');

  const handleSearch = useCallback(() => {
    setSearchKeyword(searchInput);
  }, [searchInput]);

  const handleReset = useCallback(() => {
    setSearchInput('');
    setSearchKeyword('');
    setDateFrom('');
    setDateTo('');
    setActiveFilter('all');
    setCurrentPage(1);
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="flex-1 bg-background-100">
      <div className="px-4 md:px-6 pb-4 md:pb-6">
        <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              {typeFilters.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActiveFilter(f.id)}
                  className={`px-2.5 py-1.5 rounded-full text-xs border whitespace-nowrap shrink-0 transition-colors cursor-pointer ${activeFilter === f.id ? 'bg-accent-500 text-background-50 border-accent-500' : 'border-background-300 text-foreground-600 hover:border-accent-400'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="w-56 shrink-0">
              <DateRangePicker
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            </div>

            <div className="flex items-center flex-1 min-w-0 max-w-sm border border-background-200 rounded-md overflow-hidden bg-background-50 focus-within:border-primary-400 transition-colors">
              <input
                type="text"
                placeholder="搜索商品名称、订单号、售后单号"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                className="flex-1 py-2 px-3 text-sm outline-none bg-transparent min-w-0"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); setSearchKeyword(''); }}
                  className="px-2 text-foreground-400 hover:text-foreground-600 shrink-0"
                >
                  <i className="ri-close-line" />
                </button>
              )}
              <button
                type="button"
                onClick={handleSearch}
                className="px-3 py-2 text-foreground-500 hover:text-primary-600 border-l border-background-200 shrink-0"
              >
                <i className="ri-search-line" />
              </button>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-background-200 text-xs text-foreground-500 hover:text-foreground-700 hover:bg-background-50 transition-colors shrink-0 whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                重置
              </button>
            )}

            <span className="ml-auto text-xs text-foreground-500 whitespace-nowrap">
              共 <strong className="text-foreground-900">{total}</strong> 条
            </span>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
            <p className="text-sm text-foreground-500 mt-3">正在加载售后列表...</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500 mb-2">{loadError}</p>
            <button
              type="button"
              onClick={() => void fetchList(currentPage)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              重新加载
            </button>
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-service-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500 mb-2">没有找到符合条件的售后记录</p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                清除筛选条件
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {list.map((req) => {
                const typeKey = String(req.type || '');
                const stateKey = String(req.state || '');
                const type = typeMap[typeKey] || {
                  label: typeKey || '售后',
                  icon: 'ri-service-line',
                  color: 'bg-background-100 text-foreground-700',
                };
                const status = statusMap[stateKey] || {
                  label: stateKey || '未知',
                  icon: 'ri-question-line',
                  color: 'bg-background-100 text-foreground-700',
                };
                const price = Number(req.price ?? 0) || 0;
                const quantity = Number(req.quantity ?? 0) || 0;
                const deptName = req.office?.title || '';
                const rejectReason = req.back_remark || '';
                const rowId = String(req.id);

                return (
                  <div key={rowId} className="bg-white rounded-xl border border-background-200 overflow-hidden">
                    <div className="px-5 py-3 border-b border-background-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <i className="ri-calendar-check-line text-accent-500" />
                        <span className="text-sm font-semibold text-foreground-900">
                          售后申请 · {formatDate(req.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${type.color}`}>
                          <i className={type.icon} />
                          {type.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          <i className={status.icon} />
                          {status.label}
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        <span className="text-xs text-foreground-400">单号：{req.back_sn || req.id}</span>
                        {deptName && (
                          <span className="text-xs text-foreground-400 inline-flex items-center gap-1">
                            <i className="ri-hospital-line" />
                            {deptName}
                          </span>
                        )}
                      </div>

                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-900">{req.goods_name || '-'}</p>
                          <p className="text-xs text-foreground-500 mt-0.5">
                            {req.sku_name || '-'} · ¥{price.toFixed(2)}/{req.unit || '-'} · 数量 {quantity}
                          </p>
                          {req.reason && (
                            <p className="text-xs text-foreground-600 mt-1.5 leading-relaxed">
                              <span className="text-foreground-500">原因：</span>{req.reason}
                            </p>
                          )}
                          {req.remark && (
                            <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">{req.remark}</p>
                          )}
                        </div>
                      </div>

                      {rejectReason && stateKey === '已拒绝' && (
                        <div className="p-2.5 bg-primary-50 rounded-lg text-xs text-primary-700 mb-2">
                          <i className="ri-error-warning-line mr-1" />
                          驳回原因：{rejectReason}
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2 border-t border-background-100">
                        <div className="flex items-center gap-2 text-xs text-foreground-500">
                          <span>关联订单：</span>
                          {req.order_id ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/order/${req.order_id}`)}
                              className="text-primary-600 hover:text-primary-700 font-medium"
                            >
                              {req.order_sn || req.order_id}
                            </button>
                          ) : (
                            <span>{req.order_sn || '-'}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {req.order_id && (
                            <button
                              type="button"
                              onClick={() => navigate(`/order/${req.order_id}`)}
                              className="px-3 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-600 hover:bg-background-50 whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-file-list-3-line mr-1" /> 查看订单
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleExpand(rowId)}
                            className="px-3 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-600 hover:bg-background-50 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
                          >
                            {expandedId === rowId ? '收起流程' : '查看售后进度'}
                            <i className={expandedId === rowId ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {expandedId === rowId && (() => {
                      const completedCount = getBackCompletedCount(stateKey);
                      const isRejected = stateKey === '已拒绝';
                      const stepTimes: Record<number, string | undefined> = {
                        0: req.created_at ? String(req.created_at) : undefined,
                        1: req.check_time ? String(req.check_time) : undefined,
                        2: req.change_time ? String(req.change_time) : undefined,
                        3: req.complete_time ? String(req.complete_time) : undefined,
                      };
                      const stepExtras: Record<number, string | undefined> = {
                        2: '线下进行退换货',
                      };

                      return (
                        <div className="border-t border-background-200 px-4 md:px-6 py-6 bg-background-50">
                          <h4 className="text-sm font-semibold text-foreground-900 mb-5">售后处理进度</h4>
                          <div className="w-full overflow-x-auto">
                            <div className="flex items-start min-w-[480px]">
                              {BACK_STEPS.map((step, index) => {
                                const isDone = index < completedCount;
                                const isCurrent = !isDone && index === completedCount && !isRejected;
                                const isCurrentRejected = isRejected && index === 1;
                                const isLast = index === BACK_STEPS.length - 1;
                                const stepTime =
                                  isDone || isCurrent || isCurrentRejected
                                    ? stepTimes[index]
                                    : undefined;
                                const stepExtra =
                                  isDone || isCurrent ? stepExtras[index] : undefined;

                                return (
                                  <div key={step.key} className="flex-1 flex flex-col items-center relative">
                                    <div className="flex items-center w-full">
                                      <div
                                        className={`flex-1 h-px transition-colors ${
                                          index === 0
                                            ? 'invisible'
                                            : isDone
                                              ? 'bg-accent-500'
                                              : 'bg-background-200'
                                        }`}
                                      />
                                      <div className="shrink-0 relative">
                                        {isDone ? (
                                          <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
                                            <i className="ri-check-line text-white text-sm" />
                                          </div>
                                        ) : isCurrent ? (
                                          <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center ring-4 ring-accent-100">
                                            <span className="text-xs font-bold text-white leading-none">
                                              {index + 1}
                                            </span>
                                          </div>
                                        ) : isCurrentRejected ? (
                                          <div
                                            className="w-7 h-7 rounded-full flex items-center justify-center ring-2 ring-red-400"
                                            style={{ backgroundColor: '#ed4014' }}
                                          >
                                            <i className="ri-close-line text-white text-sm" />
                                          </div>
                                        ) : (
                                          <div className="w-7 h-7 rounded-full border-2 border-background-200 bg-white flex items-center justify-center">
                                            <span className="text-xs text-foreground-400 font-medium leading-none">
                                              {index + 1}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <div
                                        className={`flex-1 h-px transition-colors ${
                                          isLast
                                            ? 'invisible'
                                            : isDone && completedCount > index + 1
                                              ? 'bg-accent-500'
                                              : 'bg-background-200'
                                        }`}
                                      />
                                    </div>

                                    <div className="mt-2 text-center px-1">
                                      <span
                                        className={`text-xs font-medium leading-tight ${
                                          isDone || isCurrent
                                            ? 'text-foreground-800'
                                            : isCurrentRejected
                                              ? 'text-red-500'
                                              : 'text-foreground-400'
                                        }`}
                                      >
                                        {step.label}
                                      </span>
                                      {stepTime && (
                                        <p className="text-[10px] text-foreground-400 mt-0.5 leading-tight whitespace-nowrap">
                                          {formatDatetime(stepTime)}
                                        </p>
                                      )}
                                      {!stepTime && stepExtra && (
                                        <p className="text-[10px] text-foreground-400 mt-0.5 leading-tight">
                                          {stepExtra}
                                        </p>
                                      )}
                                      {isCurrentRejected && rejectReason && (
                                        <p className="text-[10px] text-red-500 mt-0.5 leading-tight max-w-[120px] mx-auto">
                                          {rejectReason}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            <Pagination
              className="mt-4"
              page={currentPage}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
